/**
 * User settings (persisted in localStorage) and the performance log used to
 * compare backends. Both are tiny pub/sub stores so components can react
 * without threading props through the whole tree.
 */

const SETTINGS_KEY = 'wordsninja-settings';

export const BACKENDS = [
  {
    id: 'rust',
    name: 'Rust (rustomatic)',
    description: 'Rust port of Nutrimatic with the API built in. Default.',
    baseURL: '/api',
  },
  {
    id: 'legacy',
    name: 'C++ (Nutrimatic + Python API)',
    description: 'The original C++ find-expr wrapped by the Python FastAPI tier.',
    baseURL: '/api-legacy',
  },
];

const DEFAULT_SETTINGS = {
  backend: 'rust',
  showTiming: true,
};

let settings = loadSettings();
const settingsListeners = new Set();

function loadSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    const saved = raw ? JSON.parse(raw) : {};
    const merged = { ...DEFAULT_SETTINGS, ...saved };
    if (!BACKENDS.some((b) => b.id === merged.backend)) {
      merged.backend = DEFAULT_SETTINGS.backend;
    }
    return merged;
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export const getSettings = () => settings;

export const getBackend = () => BACKENDS.find((b) => b.id === settings.backend) || BACKENDS[0];

export const updateSettings = (patch) => {
  settings = { ...settings, ...patch };
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    // Private mode etc.: keep the in-memory value.
  }
  settingsListeners.forEach((fn) => fn(settings));
};

export const subscribeSettings = (fn) => {
  settingsListeners.add(fn);
  return () => settingsListeners.delete(fn);
};

// --- Performance log -------------------------------------------------------

const MAX_LOG = 50;
let perfLog = [];
const perfListeners = new Set();

/**
 * Record one API call. `entry` fields:
 *   backend, query, dictionary, offset, roundTripMs, serverMs (may be null),
 *   steps (may be null), cached (may be null), results, limitReached, error
 */
export const recordPerf = (entry) => {
  perfLog = [{ id: Date.now() + Math.random(), at: new Date(), ...entry }, ...perfLog].slice(0, MAX_LOG);
  perfListeners.forEach((fn) => fn(perfLog));
};

export const getPerfLog = () => perfLog;

export const clearPerfLog = () => {
  perfLog = [];
  perfListeners.forEach((fn) => fn(perfLog));
};

export const subscribePerf = (fn) => {
  perfListeners.add(fn);
  return () => perfListeners.delete(fn);
};

export const formatMs = (ms) => {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return '–';
  if (ms < 10) return `${ms.toFixed(1)} ms`;
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
};
